import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BASE_URL,
  getLocalStorageValue,
  getUserId,
} from "../../components/Helpers/helpers";

export const transactionApi = createApi({
  reducerPath: "transactionApi",

  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
  }),

  tagTypes: ["Transactions", "TransactionDetail", "TransactionStatus"],

  endpoints: (builder) => ({
    getTransactionList: builder.query({
      query: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return `/invoice?${query}`;
      },
      providesTags: ["Transactions"],
    }),

    getAllTransactionList: builder.query({
      query: (params = {}) => {
        const OrganisationID = getLocalStorageValue("OrganisationID");
        const currentUser = getUserId();
        const baseParams = {
          OrganisationID,
          page: params.page || 1,
          role_name: currentUser?.Role?.Name || "User",
          UserId: currentUser?.UserID,
        };

        const queryParams = { ...baseParams, ...params };

        if (params.filters && Array.isArray(params.filters)) {
          const cleanedFilters = params.filters.filter((filter) => {
            if (!filter || !filter.name) return false;

            if (
              filter.value === "" ||
              filter.value === null ||
              filter.value === undefined
            ) {
              return false;
            }

            if (
              typeof filter.value === "object" &&
              !Array.isArray(filter.value)
            ) {
              return Object.keys(filter.value).length > 0;
            }

            return true;
          });

          if (cleanedFilters.length > 0) {
            queryParams.filters = JSON.stringify(cleanedFilters);
          }
        }

        if (params.sort && Array.isArray(params.sort)) {
          const cleanedSort = params.sort.filter((s) => s.field && s.order);

          if (cleanedSort.length > 0) {
            queryParams.sort = JSON.stringify(cleanedSort);
          }
        }

        Object.keys(queryParams).forEach((key) => {
          if (
            queryParams[key] === "" ||
            queryParams[key] === null ||
            queryParams[key] === undefined
          ) {
            delete queryParams[key];
          }
        });

        const queryString = new URLSearchParams(queryParams).toString();

        return `/bankFeed?${queryString}`;
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,

      providesTags: ["Transactions"],
    }),

    getTransactionDetail: builder.query({
      query: (id) => ({
        url: `/bankFeed/transaction-detail/${id}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, id) => [{ type: "TransactionDetail", id }],
    }),

    updateTransactionUser: builder.mutation({
      query: ({ BankTransactionID, UserID, CurrentLoginedUserId }) => ({
        url: `/bankFeed/update-transaction-user`,
        method: "POST",
        body: { BankTransactionID, UserID, CurrentLoginedUserId },
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    approveTransaction: builder.mutation({
      query: ({ BankTransactionID, currentUserId }) => ({
        url: `/bankFeed/update-transaction-status`,
        method: "POST",
        body: { BankTransactionID, currentUserId },
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    verifyTransaction: builder.mutation({
      query: ({ BankTransactionID, currentUserId }) => ({
        url: `/bankFeed/verified-transcation`,
        method: "POST",
        body: { BankTransactionID, currentUserId },
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    deleteTransaction: builder.mutation({
      query: (BankTransactionID) => ({
        url: `/bankFeed/delete-transaction-document`,
        method: "POST",
        body: { BankTransactionID },
      }),
      invalidatesTags: ["Transactions"],
    }),

    postTransactionAttachment: builder.mutation({
      query: ({ BankTransactionID, OrganisationID, FileName }) => ({
        url: `/bankFeed/${BankTransactionID}/Attachments/${FileName}`,
        method: "POST",
        body: { BankTransactionID, OrganisationID, fileName: FileName },
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    readTransactionStatus: builder.mutation({
      query: (id) => ({
        url: `/bankFeed/read-transaction-status/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["TransactionStatus"],
    }),

    updateTransactionDetails: builder.mutation({
      query: (payload) => ({
        url: `/bankFeed/transactionUpdates`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    updateTransactioBankStatementDetails: builder.mutation({
      query: (payload) => ({
        url: `/bankStatement/transactionUpdates`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Transactions", "TransactionDetail"],
    }),

    getLastBKUser: builder.query({
      query: ({ organisationId, BankStatementID }) => {
        const isAccountTransaction =
          window.location.pathname === "/account-transactions";

        return {
          url: isAccountTransaction
            ? "/spend/last-bk-user"
            : "/bankFeed/last-bk-user",
          params: {
            organisationId,
            BankStatementID,
          },
        };
      },
    }),
    getLastBKUserBank: builder.query({
      query: ({ OrganisationID, BankStatementID }) => ({
        url: "/bankStatement/last-bk-user",
        params: { OrganisationID, BankStatementID },
      }),
    }),
  }),
});

export const {
  useGetTransactionListQuery,
  useGetAllTransactionListQuery,
  useGetTransactionDetailQuery,
  useUpdateTransactionUserMutation,
  useApproveTransactionMutation,
  useVerifyTransactionMutation,
  useDeleteTransactionMutation,
  usePostTransactionAttachmentMutation,
  useReadTransactionStatusMutation,
  useUpdateTransactionDetailsMutation,
  useLazyGetTransactionDetailQuery,
  useGetLastBKUserQuery,
  useUpdateTransactioBankStatementDetailsMutation,
  useGetLastBKUserBankQuery,
} = transactionApi;
